import type { KeyboardEvent, MouseEvent, TouchEvent } from 'react';
import styled from 'styled-components';
import { plural } from '@frameio/i18n';
import { Text, theme, Toggle, ToggleSwitch } from '@frameio/vapor';
import { IconClock } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/ProjectAssetPolicyToggle';

TEXT.DEFINE({
  /** Setting title for asset lifecycle policy */
  title: 'Asset Lifecycle',
  /** Description for asset lifecycle timing */
  description: (days: number) =>
    plural(days, {
      one: 'Auto-delete assets 1 day after upload date.',
      other: `Auto-delete assets ${days} days after upload date`,
    }),
});

type ProjectAssetPolicyToggleProps = {
  days: number;
  isEnabled: boolean;
  onChange: (value: boolean) => void;
};
export const ProjectAssetPolicyToggle = ({
  days,
  isEnabled,
  onChange,
}: ProjectAssetPolicyToggleProps) => {
  const handleToggleClick = (e: MouseEvent | TouchEvent | KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(!isEnabled);
  };
  return (
    <ToggleWrapper>
      <Toggle
        block={true}
        checked={isEnabled}
        onChange={() => onChange(!isEnabled)}
      >
        <ToggleContainer
          onClick={handleToggleClick}
          onKeyDown={handleToggleClick}
          onTouchStart={handleToggleClick}
        >
          <ToggleInfo>
            <IconClock color={theme.colors.icon.primary} />
            <ToggleLabels>
              <Text variant="body">{TEXT.title}</Text>
              <Text
                color="tertiary"
                style={{ textAlign: 'left' }}
                variant="secondary"
              >
                {TEXT.description(days)}
              </Text>
            </ToggleLabels>
          </ToggleInfo>
          <ToggleSwitch />
        </ToggleContainer>
      </Toggle>
    </ToggleWrapper>
  );
};
const ToggleWrapper = styled.div`
  background: ${theme.colors.background.card};
  border-bottom-left-radius: ${theme.radii['6']};
  border-bottom-right-radius: ${theme.radii['6']};
  padding-block: ${theme.spaces[16]};
  padding-inline: ${theme.spaces[12]} ${theme.spaces[16]};
  width: 100%;
`;
const ToggleContainer = styled.div`
  align-items: center;
  display: grid;
  gap: ${theme.spaces[16]};
  grid-template-columns: 1fr auto;
  width: 100%;
`;
const ToggleInfo = styled.div`
  align-items: center;
  display: grid;
  gap: ${theme.spaces[8]};
  grid-template-columns: auto 1fr;
`;
const ToggleLabels = styled.div`
  align-items: start;
  display: flex;
  flex-direction: column;
  gap: ${theme.spaces[2]};
  justify-content: start;
  text-align: start;
`;
